import { create } from 'zustand'
import i18n, { LANGUAGE_STORAGE_KEY as LANG_KEY } from '../lib/i18n'
import {
  DEFAULT_HIDDEN,
  DEFAULT_VIEW_ORDER,
  reconcileViewOrder,
  SIDEBAR_VIEWS,
  type SidebarViewId,
} from '../lib/sidebarViews'
import {
  DEFAULT_EDITOR_TYPOGRAPHY,
  DEFAULT_FONT_PREFERENCES,
  type EditorTypography,
  type FontPreferences,
  type FontRegion,
} from '@caliu/types'
import { normalizeEditorTypography, normalizeFontPreferences, snapTypographyValue } from '@caliu/utils'
import { track, setProfileProps, AnalyticsEvent } from '../lib/analytics'
import { DEFAULT_NEW_NOTE_START, isNewNoteStart, type NewNoteStart } from '../lib/newNoteContent'

/** Pages of the Settings dialog, in the native app's order. */

/**
 * `paywall_shown {feature}` vocabulary on web (ANALYTICS.md "Paywall &
 * purchase"). `collaboration`: inviting people and an edit link (PLAN.md D6).
 */
export type PaywallFeature =
  | 'theme'
  | 'onboarding_theme'
  | 'font'
  | 'export'
  | 'bulk_export'
  | 'version_history'
  | 'account'
  | 'onboarding_pro'
  | 'server'
  | 'note_limit'
  | 'collaboration'

export type SettingsPage =
  | 'root'
  | 'account'
  | 'theme'
  | 'typography'
  | 'editor'
  | 'shortcuts'
  /** Desktop sidebar subjects (the split view); phones never open them. */
  | 'general'
  | 'notes'
  | 'about'
  | 'import'
  | 'conflicts'
  /** Pushed from Account. */
  | 'apiKeys'
  | 'devices'
  | 'connectedApps'
  /** A font region's picker, pushed from Typography. */
  | `font:${FontRegion}`
  /** The help center: `help`, then `help:<section>`, then `help:<section>/<article>`. */
  | 'help'
  | `help:${string}`

export type Theme = 'light' | 'dark' | 'system'
export type NoteSortField = 'updatedAt' | 'createdAt' | 'title' | 'manual'
export type NoteSortDirection = 'desc' | 'asc'
export type NotePreviewStyle = 'small' | 'medium' | 'large'
/** Note-list layout, independent of density (`NotePreviewStyle`). Mirrors the
 *  native app's `NoteListViewKind`. Grid rendering is a follow-up; the model
 *  + persistence land now so the setting survives and the menu can drive it. */
export type NoteListViewKind = 'grid' | 'list'
export type InspectorTab = 'info' | 'outline' | 'links' | 'attachments' | 'history'

const THEME_KEY = 'caliu-theme'
const COLOR_THEME_KEY = 'caliu-color-theme'
const COLOR_THEME_LIGHT_KEY = 'caliu-color-theme-light'
const COLOR_THEME_DARK_KEY = 'caliu-color-theme-dark'
const FONTS_KEY = 'caliu-fonts'
const EDITOR_TYPOGRAPHY_KEY = 'caliu-editor-typography'
// Same key/value as the Swift app's AppPreferences: single Enter inserts a
// line break, double Enter starts a paragraph (markdown muscle memory).
const MARKDOWN_ENTER_KEY = 'caliu-markdown-enter'
const MAX_WIDTH_KEY = 'caliu-editor-max-width-enabled' // same key as Swift
// Same key and values as Swift and Android: what a new note starts with.
const NEW_NOTE_START_KEY = 'caliu-new-note-start'
const COLLAPSIBLE_SECTIONS_KEY = 'caliu-collapsible-sections'

/** Editor settings toggles act through a class on <html> (editor.css reads them). */
function applyEditorClass(name: string, on: boolean) {
  if (typeof document !== 'undefined') document.documentElement.classList.toggle(name, on)
}
const OPEN_TAGS_KEY = 'caliu-open-tag-ids'
const SORT_FIELD_KEY = 'caliu-note-sort-field'
const PREVIEW_STYLE_KEY = 'caliu-note-preview-style'
const VIEW_KIND_KEY = 'caliu-note-list-view-kind'
const SIDEBAR_VIEW_ORDER_KEY = 'caliu-sidebar-view-order'
const SIDEBAR_VIEW_HIDDEN_KEY = 'caliu-sidebar-view-hidden'
const INSPECTOR_OPEN_KEY = 'caliu-inspector-open'
const INSPECTOR_TAB_KEY = 'caliu-inspector-tab'
const INSPECTOR_TABS: InspectorTab[] = ['info', 'outline', 'links', 'attachments', 'history']
export { LANG_KEY }

function loadLanguage(): string | null {
  return safeGetItem(LANG_KEY)
}

function loadOpenTagIds(): string[] | null {
  try {
    const stored = safeGetItem(OPEN_TAGS_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}


function safeGetItem(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}

function loadNoteSortField(): NoteSortField {
  const stored = safeGetItem(SORT_FIELD_KEY)
  if (stored === 'updatedAt' || stored === 'createdAt' || stored === 'title' || stored === 'manual') return stored
  return 'updatedAt'
}

function loadNotePreviewStyle(): NotePreviewStyle {
  const stored = safeGetItem(PREVIEW_STYLE_KEY)
  if (stored === 'small' || stored === 'medium' || stored === 'large') return stored
  // Medium, the native default: a two-line preview.
  return 'medium'
}

function loadNoteListViewKind(): NoteListViewKind {
  const stored = safeGetItem(VIEW_KIND_KEY)
  if (stored === 'grid' || stored === 'list') return stored
  return 'list'
}

function loadTheme(): Theme {
  const stored = safeGetItem(THEME_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return 'system'
}

/**
 * Per-mode color theme, with migration from the single-theme era: the legacy
 * `caliu-color-theme` key seeds both modes, so existing users keep their theme
 * in light and dark until they deliberately split them.
 */
function loadColorThemeForMode(key: string): string {
  return safeGetItem(key) || safeGetItem(COLOR_THEME_KEY) || 'caliu'
}

function loadFontPreferences(): FontPreferences {
  try {
    const raw = safeGetItem(FONTS_KEY)
    if (!raw) return cloneFontPreferences(DEFAULT_FONT_PREFERENCES)
    const parsed = JSON.parse(raw)
    return normalizeFontPreferences(parsed)
  } catch {
    return cloneFontPreferences(DEFAULT_FONT_PREFERENCES)
  }
}

function cloneFontPreferences(prefs: FontPreferences): FontPreferences {
  return {
    regions: { ...prefs.regions },
  }
}

function persistFontPreferences(prefs: FontPreferences) {
  try {
    localStorage.setItem(FONTS_KEY, JSON.stringify(prefs))
  } catch {
    // localStorage may be unavailable (private mode) — keep in-memory state.
  }
}

function loadEditorTypography(): EditorTypography {
  try {
    const raw = safeGetItem(EDITOR_TYPOGRAPHY_KEY)
    if (!raw) return { ...DEFAULT_EDITOR_TYPOGRAPHY }
    return normalizeEditorTypography(JSON.parse(raw))
  } catch {
    return { ...DEFAULT_EDITOR_TYPOGRAPHY }
  }
}

function persistEditorTypography(value: EditorTypography) {
  try {
    localStorage.setItem(EDITOR_TYPOGRAPHY_KEY, JSON.stringify(value))
  } catch {
    // private mode etc. — keep in-memory state
  }
}

function loadSidebarViewOrder(): SidebarViewId[] {
  try {
    const stored = safeGetItem(SIDEBAR_VIEW_ORDER_KEY)
    if (!stored) return DEFAULT_VIEW_ORDER.slice()
    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) return DEFAULT_VIEW_ORDER.slice()
    return reconcileViewOrder(parsed as SidebarViewId[])
  } catch {
    return DEFAULT_VIEW_ORDER.slice()
  }
}

function loadInspectorTab(): InspectorTab {
  const stored = safeGetItem(INSPECTOR_TAB_KEY)
  return INSPECTOR_TABS.includes(stored as InspectorTab) ? (stored as InspectorTab) : 'info'
}

function persistInspectorOpen(open: boolean) {
  try {
    localStorage.setItem(INSPECTOR_OPEN_KEY, open ? 'open' : 'closed')
  } catch {
    // localStorage may be unavailable (private mode) — keep in-memory state.
  }
}

function persistInspectorTab(tab: InspectorTab) {
  try {
    localStorage.setItem(INSPECTOR_TAB_KEY, tab)
  } catch {
    // localStorage may be unavailable (private mode) — keep in-memory state.
  }
}

function loadSidebarViewHidden(): SidebarViewId[] {
  try {
    const stored = safeGetItem(SIDEBAR_VIEW_HIDDEN_KEY)
    if (!stored) return DEFAULT_HIDDEN.slice()
    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) return DEFAULT_HIDDEN.slice()
    return (parsed as SidebarViewId[]).filter((id): id is SidebarViewId => id in SIDEBAR_VIEWS)
  } catch {
    return DEFAULT_HIDDEN.slice()
  }
}

interface UIState {
  activeNoteId: string | null
  theme: Theme
  colorThemeLight: string
  colorThemeDark: string
  /** Sets BOTH modes to the same theme (the default, single-theme behavior). */
  setColorTheme: (id: string) => void
  /** Sets the theme for one mode only (opt-in day/night pairing). */
  setColorThemeForMode: (mode: 'light' | 'dark', id: string) => void
  cmdPaletteOpen: boolean
  editorFullscreen: boolean
  lastCodeLanguage: string | null
  styleBarVisible: boolean
  setActiveNoteId: (id: string | null) => void
  setTheme: (theme: Theme) => void
  setCmdPaletteOpen: (open: boolean) => void
  openCmdPalette: () => void
  closeCmdPalette: () => void
  setEditorFullscreen: (fs: boolean) => void
  toggleEditorFullscreen: () => void
  setLastCodeLanguage: (lang: string | null) => void
  setStyleBarVisible: (visible: boolean) => void
  toggleStyleBar: () => void
  skipEditorFocus: boolean
  setSkipEditorFocus: (skip: boolean) => void
  pendingEditorFocus: boolean
  requestEditorFocus: () => void
  clearPendingEditorFocus: () => void
  openTagIds: string[] | null
  setOpenTagIds: (ids: string[]) => void
  noteSortField: NoteSortField
  setNoteSortField: (field: NoteSortField) => void
  notePreviewStyle: NotePreviewStyle
  setNotePreviewStyle: (style: NotePreviewStyle) => void
  noteListViewKind: NoteListViewKind
  setNoteListViewKind: (kind: NoteListViewKind) => void
  /** The Settings page on screen (the native app's Settings window), null when closed. */
  settingsPage: SettingsPage | null
  openSettings: (page?: SettingsPage) => void
  closeSettings: () => void
  fontPreferences: FontPreferences
  setFontForRegion: (region: FontRegion, fontId: string) => void
  resetFontPreferences: () => void
  editorTypography: EditorTypography
  setEditorTypography: (key: keyof EditorTypography, value: number) => void
  resetEditorTypography: () => void
  /** Single Enter = line break, double Enter = new paragraph (off by default). */
  markdownEnter: boolean
  setMarkdownEnter: (on: boolean) => void
  /** What a note created in the app starts with (an empty H1 by default, as before). */
  newNoteStart: NewNoteStart
  setNewNoteStart: (mode: NewNoteStart) => void
  /** One shot: the note just created in the app, whose first load puts the
   *  caret in its starting block (`planNewNoteStart`). Cleared on that load. */
  pendingNewNoteId: string | null
  setPendingNewNoteId: (id: string | null) => void
  /** Centre the text in a readable column (on by default, like the native apps). */
  maxWidth: boolean
  setMaxWidth: (on: boolean) => void
  /** Fold headings behind a chevron (off by default, experimental on native). */
  collapsibleSections: boolean
  setCollapsibleSections: (on: boolean) => void
  isSourceMode: boolean
  setSourceMode: (on: boolean) => void
  toggleSourceMode: () => void
  sidebarViewOrder: SidebarViewId[]
  sidebarViewHidden: SidebarViewId[]
  sidebarEditMode: boolean
  setSidebarEditMode: (on: boolean) => void
  moveView: (from: number, to: number) => void
  toggleViewHidden: (id: SidebarViewId) => void
  language: string | null
  setLanguage: (lang: string | null) => void
  /** Caliu Pro paywall. `paywallFeature` records what was gated (analytics). */
  paywallOpen: boolean
  paywallFeature: PaywallFeature | null
  openPaywall: (feature?: PaywallFeature) => void
  closePaywall: () => void
  /** The share sheet (PLAN.md task 3.5): the note it is open for, and where it was opened from. */
  shareSheet: { noteId: string; source: string } | null
  openShareSheet: (noteId: string, source: string) => void
  closeShareSheet: () => void
  /** Notes this account lost access to while they were open (the No access screen), with whether a copy was kept. */
  lostAccess: Record<string, { savedCopy: boolean }>
  markLostAccess: (noteId: string, savedCopy: boolean) => void
  /**
   * Bulk-export progress. Non-null while a multi-note export is running, which
   * puts a blocking dialog up (leaving the tab mid-ZIP loses the export).
   */
  exportProgress: { done: number; total: number } | null
  setExportProgress: (progress: { done: number; total: number } | null) => void
  /** Pro "thank you" / manage-subscription dialog (shown to existing Pro users). */
  proThanksOpen: boolean
  openProThanks: () => void
  closeProThanks: () => void
  /**
   * First-run onboarding overlay. Opened by AppShell when the completion flag
   * is absent, or from the UserMenu as a replay (replay never re-seeds the
   * first note). Closing is owned by the overlay itself on complete/skip.
   */
  onboardingOpen: boolean
  onboardingReplay: boolean
  openOnboarding: (options?: { replay?: boolean }) => void
  closeOnboarding: () => void
  /**
   * "What's new" shown again on demand, counted so each replay opens it anew.
   * The hidden way to check it in a release build: five taps on the "About"
   * title in Settings. The seen record is untouched.
   */
  whatsNewReplays: number
  replayWhatsNew: () => void
  /** Note inspector panel (Info/Outline/Links/Attachments/History). */
  inspectorOpen: boolean
  inspectorTab: InspectorTab
  toggleInspector: () => void
  openInspector: (tab?: InspectorTab) => void
  closeInspector: () => void
  setInspectorTab: (tab: InspectorTab) => void
}

export const useUIStore = create<UIState>()((set, get) => ({
  activeNoteId: null,
  theme: loadTheme(),
  colorThemeLight: loadColorThemeForMode(COLOR_THEME_LIGHT_KEY),
  colorThemeDark: loadColorThemeForMode(COLOR_THEME_DARK_KEY),
  setColorTheme: (id) => {
    try {
      localStorage.setItem(COLOR_THEME_LIGHT_KEY, id)
      localStorage.setItem(COLOR_THEME_DARK_KEY, id)
      // Keep the legacy key in sync while both modes agree, so a rollback to
      // a pre-split build still restores the user's theme.
      localStorage.setItem(COLOR_THEME_KEY, id)
    } catch {
      // Persistence is best-effort (private mode / quota) — state still updates.
    }
    set({ colorThemeLight: id, colorThemeDark: id })
  },
  setColorThemeForMode: (mode, id) => {
    const other = mode === 'light' ? get().colorThemeDark : get().colorThemeLight
    try {
      localStorage.setItem(
        mode === 'light' ? COLOR_THEME_LIGHT_KEY : COLOR_THEME_DARK_KEY,
        id,
      )
      // Per-mode picks can re-converge; keep the legacy key in sync whenever
      // both modes agree so a pre-split rollback restores the right theme.
      if (other === id) localStorage.setItem(COLOR_THEME_KEY, id)
    } catch {
      // Persistence is best-effort — state still updates.
    }
    set(mode === 'light' ? { colorThemeLight: id } : { colorThemeDark: id })
  },
  cmdPaletteOpen: false,
  editorFullscreen: false,
  lastCodeLanguage: safeGetItem('caliu-last-code-lang'),
  setLastCodeLanguage: (lang) => {
    if (lang) localStorage.setItem('caliu-last-code-lang', lang)
    else localStorage.removeItem('caliu-last-code-lang')
    set({ lastCodeLanguage: lang })
  },
  setActiveNoteId: (id) => set({ activeNoteId: id }),
  setTheme: (theme) => {
    localStorage.setItem(THEME_KEY, theme)
    set({ theme })
  },
  setCmdPaletteOpen: (open) => {
    if (open) track(AnalyticsEvent.CommandPaletteOpened, { source: 'shortcut' })
    set({ cmdPaletteOpen: open })
  },
  openCmdPalette: () => set({ cmdPaletteOpen: true }),
  closeCmdPalette: () => set({ cmdPaletteOpen: false }),
  setEditorFullscreen: (fs) => {
    set({ editorFullscreen: fs })
    track(AnalyticsEvent.EditorModeToggled, { mode: 'fullscreen', enabled: fs })
  },
  toggleEditorFullscreen: () => set((s) => {
    const next = !s.editorFullscreen
    track(AnalyticsEvent.EditorModeToggled, { mode: 'fullscreen', enabled: next })
    return { editorFullscreen: next }
  }),
  styleBarVisible: safeGetItem('caliu-style-bar') !== 'hidden',
  setStyleBarVisible: (visible) => {
    localStorage.setItem('caliu-style-bar', visible ? 'visible' : 'hidden')
    set({ styleBarVisible: visible })
    track(AnalyticsEvent.SettingChanged, { setting: 'formatting_bar', value: visible })
  },
  toggleStyleBar: () => set((s) => {
    const next = !s.styleBarVisible
    localStorage.setItem('caliu-style-bar', next ? 'visible' : 'hidden')
    track(AnalyticsEvent.SettingChanged, { setting: 'formatting_bar', value: next })
    return { styleBarVisible: next }
  }),
  skipEditorFocus: false,
  setSkipEditorFocus: (skip) => set({ skipEditorFocus: skip }),
  pendingEditorFocus: false,
  requestEditorFocus: () => set({ pendingEditorFocus: true }),
  clearPendingEditorFocus: () => set({ pendingEditorFocus: false }),
  openTagIds: loadOpenTagIds(),
  setOpenTagIds: (ids) => {
    localStorage.setItem(OPEN_TAGS_KEY, JSON.stringify(ids))
    set({ openTagIds: ids })
  },
  noteSortField: loadNoteSortField(),
  setNoteSortField: (field) => {
    localStorage.setItem(SORT_FIELD_KEY, field)
    set({ noteSortField: field })
  },
  notePreviewStyle: loadNotePreviewStyle(),
  setNotePreviewStyle: (style) => {
    localStorage.setItem(PREVIEW_STYLE_KEY, style)
    set({ notePreviewStyle: style })
    // Both axes on every change: they are orthogonal, and a report that only
    // sees the one that moved cannot answer which combinations people use.
    // Mirrors AppPreferencesStore.trackDisplayModeChanged on Swift.
    track(AnalyticsEvent.DisplayModeChanged, {
      view_kind: get().noteListViewKind,
      density: style,
    })
  },
  noteListViewKind: loadNoteListViewKind(),
  setNoteListViewKind: (kind) => {
    localStorage.setItem(VIEW_KIND_KEY, kind)
    set({ noteListViewKind: kind })
    track(AnalyticsEvent.DisplayModeChanged, {
      view_kind: kind,
      density: get().notePreviewStyle,
    })
  },
  settingsPage: null,
  openSettings: (page = 'root') => set({ settingsPage: page }),
  closeSettings: () => set({ settingsPage: null }),
  fontPreferences: loadFontPreferences(),
  setFontForRegion: (region, fontId) =>
    set((s) => {
      const next: FontPreferences = {
        regions: { ...s.fontPreferences.regions, [region]: fontId },
      }
      persistFontPreferences(next)
      return { fontPreferences: next }
    }),
  resetFontPreferences: () => {
    const next = cloneFontPreferences(DEFAULT_FONT_PREFERENCES)
    persistFontPreferences(next)
    set({ fontPreferences: next })
  },
  editorTypography: loadEditorTypography(),
  setEditorTypography: (key, value) =>
    set((s) => {
      const next: EditorTypography = { ...s.editorTypography, [key]: snapTypographyValue(key, value) }
      persistEditorTypography(next)
      return { editorTypography: next }
    }),
  resetEditorTypography: () => {
    const next: EditorTypography = { ...DEFAULT_EDITOR_TYPOGRAPHY }
    persistEditorTypography(next)
    set({ editorTypography: next })
  },
  markdownEnter: safeGetItem(MARKDOWN_ENTER_KEY) === 'true',
  setMarkdownEnter: (on) => {
    try {
      localStorage.setItem(MARKDOWN_ENTER_KEY, on ? 'true' : 'false')
    } catch {
      // localStorage may be unavailable (private mode) — keep in-memory state.
    }
    set({ markdownEnter: on })
    track(AnalyticsEvent.SettingChanged, { setting: 'markdown_enter', value: on })
  },
  newNoteStart: (() => {
    const stored = safeGetItem(NEW_NOTE_START_KEY)
    // An unknown or old value (`title`, `body`, `timestamp`) reads as the default.
    return isNewNoteStart(stored) ? stored : DEFAULT_NEW_NOTE_START
  })(),
  setNewNoteStart: (mode) => {
    try {
      localStorage.setItem(NEW_NOTE_START_KEY, mode)
    } catch {
      // localStorage may be unavailable (private mode) — keep in-memory state.
    }
    set({ newNoteStart: mode })
    track(AnalyticsEvent.SettingChanged, { setting: 'new_note_start', value: mode })
  },
  pendingNewNoteId: null,
  setPendingNewNoteId: (id) => set({ pendingNewNoteId: id }),
  maxWidth: (() => {
    const on = safeGetItem(MAX_WIDTH_KEY) !== 'false'
    applyEditorClass('caliu-no-max-width', !on)
    return on
  })(),
  setMaxWidth: (on) => {
    try {
      localStorage.setItem(MAX_WIDTH_KEY, on ? 'true' : 'false')
    } catch {
      // localStorage may be unavailable (private mode) — keep in-memory state.
    }
    applyEditorClass('caliu-no-max-width', !on)
    set({ maxWidth: on })
    track(AnalyticsEvent.SettingChanged, { setting: 'editor_max_width', value: on })
  },
  collapsibleSections: (() => {
    const on = safeGetItem(COLLAPSIBLE_SECTIONS_KEY) === 'true'
    applyEditorClass('caliu-no-fold', !on)
    return on
  })(),
  setCollapsibleSections: (on) => {
    try {
      localStorage.setItem(COLLAPSIBLE_SECTIONS_KEY, on ? 'true' : 'false')
    } catch {
      // localStorage may be unavailable (private mode) — keep in-memory state.
    }
    applyEditorClass('caliu-no-fold', !on)
    set({ collapsibleSections: on })
    track(AnalyticsEvent.SettingChanged, { setting: 'collapsible_sections', value: on })
  },
  isSourceMode: false,
  setSourceMode: (on) => {
    set({ isSourceMode: on })
    track(AnalyticsEvent.EditorModeToggled, { mode: 'source', enabled: on })
  },
  toggleSourceMode: () => set((s) => ({ isSourceMode: !s.isSourceMode })),
  sidebarViewOrder: loadSidebarViewOrder(),
  sidebarViewHidden: loadSidebarViewHidden(),
  sidebarEditMode: false,
  setSidebarEditMode: (on) => set({ sidebarEditMode: on }),
  moveView: (from, to) =>
    set((s) => {
      if (from === to) return {}
      if (from < 0 || from >= s.sidebarViewOrder.length) return {}
      if (to < 0 || to >= s.sidebarViewOrder.length) return {}
      const next = s.sidebarViewOrder.slice()
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      try {
        localStorage.setItem(SIDEBAR_VIEW_ORDER_KEY, JSON.stringify(next))
      } catch {
        // localStorage may be unavailable (private mode etc.) — keep in-memory state
      }
      return { sidebarViewOrder: next }
    }),
  toggleViewHidden: (id) =>
    set((s) => {
      const next = s.sidebarViewHidden.includes(id)
        ? s.sidebarViewHidden.filter((x) => x !== id)
        : [...s.sidebarViewHidden, id]
      try {
        localStorage.setItem(SIDEBAR_VIEW_HIDDEN_KEY, JSON.stringify(next))
      } catch {
        // localStorage may be unavailable (private mode etc.) — keep in-memory state
      }
      return { sidebarViewHidden: next }
    }),
  language: loadLanguage(),
  setLanguage: (lang) => {
    try {
      if (lang) {
        localStorage.setItem(LANG_KEY, lang)
      } else {
        localStorage.removeItem(LANG_KEY)
      }
    } catch {
      // private mode etc. — keep in-memory state
    }
    // When `lang` is null we want i18next to re-detect from navigator.
    // The detector reads localStorage first; since we cleared the key, it
    // falls through to the next detector in the chain.
    if (lang) {
      void i18n.changeLanguage(lang)
    } else {
      // Re-run language detection by passing `undefined`.
      void i18n.changeLanguage(undefined)
    }
    set({ language: lang })
    // User-initiated language change (init uses i18next detection, not this).
    const resolved = lang ?? i18n.resolvedLanguage ?? 'en'
    track(AnalyticsEvent.LanguageChanged, { language: resolved })
    setProfileProps({ locale: resolved })
  },
  paywallOpen: false,
  paywallFeature: null,
  openPaywall: (feature) => {
    // Single choke point for every Pro gate (client + server 402), mirroring
    // Swift's `AppState.requestPaywall`.
    track(AnalyticsEvent.PaywallShown, { feature: feature ?? null })
    set({ paywallOpen: true, paywallFeature: feature ?? null })
  },
  closePaywall: () => set({ paywallOpen: false }),
  shareSheet: null,
  openShareSheet: (noteId, source) => set({ shareSheet: { noteId, source } }),
  closeShareSheet: () => set({ shareSheet: null }),
  lostAccess: {},
  markLostAccess: (noteId, savedCopy) =>
    set((s) => ({
      lostAccess: {
        ...s.lostAccess,
        // A copy reported by either notice stays reported.
        [noteId]: { savedCopy: savedCopy || !!s.lostAccess[noteId]?.savedCopy },
      },
    })),
  exportProgress: null,
  setExportProgress: (progress) => set({ exportProgress: progress }),
  proThanksOpen: false,
  openProThanks: () => set({ proThanksOpen: true }),
  closeProThanks: () => set({ proThanksOpen: false }),
  onboardingOpen: false,
  onboardingReplay: false,
  openOnboarding: (options) =>
    set({ onboardingOpen: true, onboardingReplay: options?.replay ?? false }),
  closeOnboarding: () => set({ onboardingOpen: false, onboardingReplay: false }),
  whatsNewReplays: 0,
  replayWhatsNew: () => set((s) => ({ whatsNewReplays: s.whatsNewReplays + 1 })),
  inspectorOpen: safeGetItem(INSPECTOR_OPEN_KEY) === 'open',
  inspectorTab: loadInspectorTab(),
  toggleInspector: () => set((s) => {
    const next = !s.inspectorOpen
    persistInspectorOpen(next)
    return { inspectorOpen: next }
  }),
  openInspector: (tab) => set((s) => {
    persistInspectorOpen(true)
    if (tab) persistInspectorTab(tab)
    return { inspectorOpen: true, inspectorTab: tab ?? s.inspectorTab }
  }),
  closeInspector: () => {
    persistInspectorOpen(false)
    set({ inspectorOpen: false })
  },
  setInspectorTab: (tab) => {
    persistInspectorTab(tab)
    set({ inspectorTab: tab })
  },
}))
